import { useEffect } from "react";

const PIXEL_ID = "3CZxnWGDqD3cLJ5Jv8kj1R";
const LOADER_MARK = "__oaiq_loader_injected";
const PAGE_VIEWED_MARK = "__oaiq_page_viewed_fired";

/**
 * Injects the official OpenAI Ads Pixel loader exactly as provided by
 * OpenAI Ads Manager, and calls oaiq("init", ...). Idempotent — subsequent
 * calls are no-ops.
 *
 * The pixel is loaded following the official OpenAI implementation. It is
 * NOT gated behind our custom "Accept" cookie click — the pixel handles
 * consent requirements per OpenAI's own spec. GA4 consent behavior is
 * unaffected and continues to be managed separately via tracking.js.
 *
 * The SDK reads the current URL and picks up any ?oppref=... attribution
 * parameter present (first-party attribution). We never strip or modify
 * oppref.
 */
function loadOpenAIAdsPixel() {
  if (typeof window === "undefined") return;
  if (window[LOADER_MARK]) return;
  window[LOADER_MARK] = true;

  // Official OpenAI Ads Pixel snippet (from OpenAI Ads Manager).
  // eslint-disable-next-line no-unused-expressions
  !(function (w, d, s, u) {
    if (w.oaiq) return;
    const q = function () {
      q.q.push(arguments);
    };
    q.q = [];
    w.oaiq = q;
    const j = d.createElement(s);
    j.async = 1;
    j.src = u;
    const f = d.getElementsByTagName(s)[0];
    f.parentNode.insertBefore(j, f);
  })(window, document, "script", "https://bzrcdn.openai.com/sdk/oaiq.min.js");

  window.oaiq("init", { pixelId: PIXEL_ID, debug: false });
}

/**
 * Fires the OpenAI Pixel `page_viewed` measurement event exactly once
 * per actual page view. Guarded against React StrictMode double-invoke
 * and any component re-render.
 */
function firePageViewedOnce() {
  try {
    if (typeof window === "undefined") return;
    if (window[PAGE_VIEWED_MARK]) return;
    if (typeof window.oaiq !== "function") return;
    window[PAGE_VIEWED_MARK] = true;
    window.oaiq("measure", "page_viewed", { type: "contents" });
  } catch (e) {
    // silent — tracking must never break UX
  }
}

/**
 * Mounted once globally. Loads the pixel per the official OpenAI
 * implementation and fires `page_viewed` exactly once. Does NOT strip
 * any URL parameters (oppref attribution is preserved). Never sends PII.
 */
export default function OpenAIAdsPixel() {
  useEffect(() => {
    loadOpenAIAdsPixel();
    firePageViewedOnce();
  }, []);
  return null;
}
